// import { RouterOutputs } from "@/services/trpc";

import { Service, Job } from "@/services/trpc/types";
import {
  addMinutes,
  eachDayOfInterval,
  format,
  isWithinInterval,
  parse,
  formatDistance,
} from "date-fns";

interface OfficeSchedule {
  monday: { start: string; end: string };
  tuesday: { start: string; end: string };
  wednesday: { start: string; end: string };
  thursday: { start: string; end: string };
  friday: { start: string; end: string };
  saturday: { start: string; end: string };
  sunday: { start: string; end: string };
}

interface AvailableSlot {
  start: Date;
  end: Date;
}

interface SuggestedOption {
  title: string;
  subtitle: string;
  caption: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

// type Service = RouterOutputs["services"]["list"][0];
// type Job = RouterOutputs["jobs"]["list"][0];

const mockOfficeSchedule: OfficeSchedule = {
  monday: { start: "09:00", end: "17:00" },
  tuesday: { start: "09:00", end: "17:00" },
  wednesday: { start: "09:00", end: "17:00" },
  thursday: { start: "09:00", end: "17:00" },
  friday: { start: "09:00", end: "17:00" },
  saturday: { start: "10:00", end: "14:00" },
  sunday: { start: "", end: "" },
};

export function findBestOptions(
  service: Service,
  jobs: Job[],
  dateRangeStart: Date,
  dateRangeEnd: Date,
): SuggestedOption[] {
  const availableSlots = generateAvailableSlots(
    service.durationMinutes || 30,
    mockOfficeSchedule,
    jobs,
    dateRangeStart,
    dateRangeEnd,
  );

  return sortAndLimitSlots(availableSlots);
}

function generateAvailableSlots(
  serviceDuration: number,
  officeSchedule: OfficeSchedule,
  jobs: Job[],
  start: Date,
  end: Date,
): AvailableSlot[] {
  console.log({
    serviceDuration,
    officeSchedule,
    jobs,
    start,
    end,
  });
  const availableSlots: AvailableSlot[] = [];
  const days = eachDayOfInterval({ start, end });

  days.forEach((day: Date) => {
    const dayOfWeek = format(day, "EEEE").toLowerCase() as keyof OfficeSchedule;
    const daySchedule = officeSchedule[dayOfWeek];

    if (daySchedule.start && daySchedule.end) {
      const dayStart = parse(daySchedule.start, "HH:mm", day);
      const dayEnd = parse(daySchedule.end, "HH:mm", day);

      let currentSlotStart = dayStart;
      while (addMinutes(currentSlotStart, serviceDuration) <= dayEnd) {
        const currentSlotEnd = addMinutes(currentSlotStart, serviceDuration);

        if (isSlotAvailable(currentSlotStart, currentSlotEnd, jobs)) {
          availableSlots.push({ start: currentSlotStart, end: currentSlotEnd });
        }

        currentSlotStart = addMinutes(currentSlotStart, 30); // 30-minute increments
      }
    }
  });

  return availableSlots;
}

function isSlotAvailable(slotStart: Date, slotEnd: Date, jobs: Job[]): boolean {
  return !jobs.some((job) => {
    if (!job.startTime || !job.endTime) {
      return false;
    }
    return (
      isWithinInterval(slotStart, {
        start: new Date(job.startTime),
        end: new Date(job.endTime),
      }) ||
      isWithinInterval(slotEnd, {
        start: new Date(job.startTime),
        end: new Date(job.endTime),
      })
    );
  });
}

function sortAndLimitSlots(availableSlots: AvailableSlot[]): SuggestedOption[] {
  // For now, we'll just return the first 3 available slots
  // In a real implementation, you'd want to sort based on proximity to the given address
  return availableSlots.slice(0, 100).map((slot) => ({
    title: `${format(slot.start, "EEE, MMM d")} | ${format(
      slot.start,
      "h:mm a",
    )} - ${format(slot.end, "h:mm a")}`,
    subtitle: "Available Slot",
    caption: "This time slot is available based on your preferences.",
    coordinates: { latitude: 40.7128, longitude: -74.006 }, // Default to New York City coordinates
  }));
}

export function getDistance(date: Date): string {
  return formatDistance(new Date(), date);
}
